/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ModalController } from "../../../model/navigation";
import { InputAction } from "../../../model/enums/InputAction";
import {
  cycleOptionValue,
  dismissSeriesRecordingOptions,
  focusDown,
  focusLeft,
  focusRight,
  focusUp,
  openOptionValue,
  RecordingOptionListType,
  saveSeriesRecordingOptions,
  selectSeriesRecordingFocus,
  selectSeriesRecordingOptions,
  SAVE_ACTION_INDEX
} from "../../../viewModel/myHub/seriesRecordingOptions";
import type { UnityAppDispatch, UnityRootState } from "../../../viewModel";

/** Modal name constant for the SeriesRecordingOptions modal. */
export const SERIES_RECORDING_OPTIONS_MODAL_NAME: string =
  "SERIES_RECORDING_OPTIONS";

/**
 * ModalController for the SeriesRecordingOptionsOverlay.
 *
 * D-pad routing:
 *   NAV_UP/DOWN/LEFT/RIGHT → move focus (delegated to the Redux slice)
 *   NAV_OK                 → activate the focused row, or Save/Cancel
 *   BACK                   → dismiss (treated as Cancel)
 *
 * All handlers return true to consume the input so it never reaches the
 * screen behind the overlay.
 */
export class SeriesRecordingOptionsOverlayController extends ModalController {
  private readonly mDispatch: UnityAppDispatch;
  private readonly mGetState: () => UnityRootState;

  public constructor(
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ) {
    super(SERIES_RECORDING_OPTIONS_MODAL_NAME);
    this.mDispatch = dispatch;
    this.mGetState = getState;
  }

  protected initialize(): void {
    this.registerHandler(InputAction.NAV_UP, (): boolean => {
      this.mDispatch(focusUp());
      return true;
    });

    this.registerHandler(InputAction.NAV_DOWN, (): boolean => {
      this.mDispatch(focusDown());
      return true;
    });

    this.registerHandler(InputAction.NAV_LEFT, (): boolean => {
      this.mDispatch(focusLeft());
      return true;
    });

    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => {
      this.mDispatch(focusRight());
      return true;
    });

    this.registerHandler(InputAction.NAV_OK, (): boolean => {
      const state = this.mGetState();
      const focus = selectSeriesRecordingFocus(state);

      if (focus.zone === "rows") {
        const option = selectSeriesRecordingOptions(state)[focus.index];
        if (option !== undefined) {
          if (
            option.optionListType === RecordingOptionListType.KeepAtMost ||
            option.optionListType === RecordingOptionListType.KeepUntil ||
            option.optionListType === RecordingOptionListType.Channel ||
            option.optionListType === RecordingOptionListType.StartRecording ||
            option.optionListType === RecordingOptionListType.StopRecording
          ) {
            this.mDispatch(openOptionValue(option.optionListType));
          } else {
            this.mDispatch(
              cycleOptionValue({
                optionListType: option.optionListType,
                direction: "next"
              })
            );
          }
        }
      } else if (focus.index === SAVE_ACTION_INDEX) {
        // Save maps the selections back to the backend shape and PUTs them
        // before dismissing the overlay with reason "save".
        this.mDispatch(saveSeriesRecordingOptions());
      } else {
        this.mDispatch(dismissSeriesRecordingOptions("cancel"));
      }

      return true;
    });

    this.registerHandler(InputAction.BACK, (): boolean => {
      this.mDispatch(dismissSeriesRecordingOptions("cancel"));
      return true;
    });
  }
}
